import {
  AddToCartResponse,
  CarouselResponse,
  CommonResponse,
  CountryLanguageResponse,
  HomepageContent,
  NavigationHeaderResponse,
  SafetyAttributeResponse,
  Sku,
} from "../types";

declare let window: ProductPage;

const handleResponse = async (res: Response) => {
  if (!res.ok) {
    throw new Error(
      `API HTTP error! URL: ${res.url}, status: ${res.status}, message: ${res.statusText}`
    );
  }

  try {
    return await res.json();
  } catch (error) {
    throw new Error(
      `API Parsing Error. Error parsing response as JSON. URL: ${res.url}, status: ${res.status}, error: ${error}`
    );
  }
};

export const addItemToCartRequest = (
  skuId: string,
  quantity: number
): Promise<AddToCartResponse> => {
  const endpoint = `/marketplace/api/cart/items?skuId=${skuId}&quantity=${quantity}&language=${window.currentLang}`;

  return fetch(endpoint, {
    method: "POST",
  }).then(handleResponse);
};

export const buyNowRequest = (skuId: string, quantity: number) => {
  const endpoint = `/marketplace/api/cart/items/buy-now?skuId=${skuId}&language=${window.currentLang}&quantity=${quantity}`;

  return fetch(endpoint, {
    method: "POST",
  }).then(handleResponse);
};

export const removeItemFromCartRequest = (
  skuId: string,
  quantity: string
): Promise<CommonResponse> =>
  fetch(
    `/marketplace/api/cart/items/remove/${skuId}?quantity=${quantity}&language=${window.currentLang}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
    }
  ).then(handleResponse);

export const updateCartItemQuantityRequest = (
  skuId: string,
  quantity: number
) =>
  fetch(
    `/marketplace/api/cart/items/${skuId}?quantity=${quantity}&language=${window.currentLang}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
    }
  ).then(handleResponse);

export const getSearchRecommendations = async (query: string) => {
  const controller = new AbortController();
  const signal = controller.signal;
  const timeout = 5000;

  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(
    `/marketplace/search/recommendations?query=${encodeURIComponent(
      query
    )}&language=${window.currentLang}`,
    { signal }
  )
    .then((res) => {
      clearTimeout(timeoutId);
      return res;
    })
    .then(handleResponse);
};

export const getSafetyAttributes = (
  skuId: string,
  language: string
): Promise<SafetyAttributeResponse> =>
  fetch(
    `/marketplace/api/sku/${skuId}/safety-attributes/${window.currentCountry}/${language}`
  ).then(handleResponse);

export const getTranslatedSafetyAttributes = (
  productId: string,
  skuId: string
): Promise<SafetyAttributeResponse> =>
  fetch(
    `/marketplace/api/translation/skuSafetyAttributes/${productId}/${skuId}?language=${window.currentLang}`
  ).then(handleResponse);

export const getHomepageHeroData = (
  commaSeparatedHeroSizes: string,
  commaSeparatedImageFormats: string
): Promise<HomepageContent> => {
  const endpoint = `/marketplace/api/home/hero?language=${window.currentLang}&sizes=${commaSeparatedHeroSizes}&formats=${commaSeparatedImageFormats}`;
  return new Promise((resolve, reject) => {
    if (!cmsDataExistsForCurrentLanguage(endpoint)) {
      fetch(endpoint)
        .then(handleResponse)
        .then((homeCmsData: HomepageContent) => {
          sessionStorage.setItem(
            `CMS_${window.currentLang}:${endpoint}`,
            JSON.stringify(homeCmsData)
          );
          resolve(homeCmsData);
        })
        .catch((error) => {
          reject(error);
        });
    } else {
      const sessionData = sessionStorage.getItem(
        `CMS_${window.currentLang}:${endpoint}`
      );
      if (sessionData) {
        const homeCmsData = JSON.parse(sessionData);
        resolve(homeCmsData);
      }
    }
  });
};

export const getPromotionalTilesData = (
  commaSeparatedCmsImageSizes: string,
  commaSeparatedImageFormats: string
): Promise<HomepageContent> => {
  const endpoint = `/marketplace/api/home/promo?language=${window.currentLang}&sizes=${commaSeparatedCmsImageSizes}&formats=${commaSeparatedImageFormats}`;
  return new Promise((resolve, reject) => {
    if (!cmsDataExistsForCurrentLanguage(endpoint)) {
      fetch(endpoint)
        .then(handleResponse)
        .then((promotionalTilesData: HomepageContent) => {
          sessionStorage.setItem(
            `CMS_${window.currentLang}:${endpoint}`,
            JSON.stringify(promotionalTilesData)
          );
          resolve(promotionalTilesData);
        })
        .catch((error) => {
          reject(error);
        });
    } else {
      const sessionData = sessionStorage.getItem(
        `CMS_${window.currentLang}:${endpoint}`
      );
      if (sessionData) {
        const promotionalTilesData = JSON.parse(sessionData);
        resolve(promotionalTilesData);
      }
    }
  });
};

function cmsDataExistsForCurrentLanguage(endpoint: string) {
  return !!sessionStorage.getItem(`CMS_${window.currentLang}:${endpoint}`);
}

export const getTranslations = (url: string) => {
  return fetch(url)
    .then((res) => {
      if (!res.ok) {
        throw new Error(
          `Translation API HTTP error! URL: ${res.url}, status: ${res.status}, message: ${res.statusText}`
        );
      }
      return res;
    })
    .then((res) => res.text());
};

export const getItemsFromOtherSellers = (productId: string): Promise<Sku[]> =>
  fetch(`/marketplace/api/multi-merchant/productId/${productId}`).then(
    handleResponse
  );

export const getCountryLanguages = (
  language = window.currentLang
): Promise<CountryLanguageResponse> =>
  fetch(`/marketplace/api/country-languages?language=${language}`).then(
    handleResponse
  );

export const getPaymentMethodList = (country?: string): Promise<string[]> =>
  fetch(
    `/marketplace/api/payment-methods${country ? `?country=${country}` : ""}`
  ).then(handleResponse);

export const getCarouselData = (url: string): Promise<CarouselResponse> =>
  fetch(url).then(handleResponse);

export const getSiteNavigationData = (
  language: string
): Promise<NavigationHeaderResponse> =>
  fetch(`/marketplace/api/site-navigation/header?language=${language}`).then(
    handleResponse
  );
